"use client";

import React, { useState, useEffect } from "react";
import axios from 'axios';
import Image from "next/image";
import Link from "next/link";
import { FaFacebookF, FaInstagram, FaWhatsapp, FaTiktok } from 'react-icons/fa';
import { FiSend, FiPhone } from 'react-icons/fi';
import { Loader2 as LucideLoader, AlertCircle as Alert, CheckCircle as Check } from "lucide-react";
import { API_BASE_URL } from '@/app/lib/api';

export default function Footer() {
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<{ message: string; isError: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/categories`);
        const data = res.data;
        if (Array.isArray(data)) {
          const names = data.map(item =>
            typeof item === 'string' ? item : (item.name || item.title)
          ).filter(Boolean);
          setCategories(Array.from(new Set(names)));
        }
      } catch (err) {
        console.error('Failed to load categories', err);
      }
    };
    fetchCategories();
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 8) {
      setStatus({ message: 'Le numéro doit contenir 8 chiffres.', isError: true });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/newsletters`, { phone: digits });
      setStatus({ message: 'Inscription réussie !', isError: false });
      setPhone('');
    } catch (error: any) {
      setStatus({
        message: error.response?.data?.message || 'Une erreur est survenue.',
        isError: true
      });
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const makeSlug = (s: string) => encodeURIComponent(String(s).toLowerCase().trim().replace(/\s+/g, '-'));

  return (
    <footer className="relative w-full overflow-hidden bg-slate-50 pt-6 sm:pt-20">
      {/* --- WAVE EFFECT --- */}
      <div className="absolute top-0 left-0 w-full rotate-180 leading-[0] overflow-hidden">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-[calc(100%+1.3px)] h-[30px] sm:h-[60px] lg:h-[80px]"
          fill="#a81b8d"
        >
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5,73.84-4.36,147.54,16.88,218.2,35.26,69.25,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"></path>
        </svg>
      </div>

      <div className="w-full bg-[#a81b8d] text-white">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">

          <div className="grid grid-cols-2 gap-12 sm:grid-cols-2 lg:grid-cols-4 items-start text-left">

            {/* Quick Links */}
            <div className="flex flex-col items-start">
              <h2 className="text-xl font-extrabold tracking-tight mb-6 border-b-2 border-white/20 pb-2 w-fit">Liens Rapides</h2>
              <nav className="flex flex-col items-start gap-4 w-full">
                {[
                  { href: '/', label: 'Accueil' },
                  { href: '/nos-produits', label: 'Nos Produits' },
                  { href: '/wishlist', label: 'Liste de Souhaits' },
                  { href: '/contact', label: 'Contactez-nous' },
                ].map((l) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    className="group flex items-center justify-start text-sm text-purple-100 transition-colors hover:text-white"
                  >
                    <span className="mr-2 h-1.5 w-1.5 rounded-full bg-white opacity-40 transition-all group-hover:w-3 group-hover:opacity-100" />
                    {l.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Categories Section */}
            <div className="flex flex-col items-start">
              <h4 className="mb-6 text-lg font-bold uppercase tracking-wider border-b-2 border-white/20 pb-2 w-fit">Nos Catégories</h4>
              <nav className="grid grid-cols-1 gap-x-4 gap-y-3 w-full text-left">
                {[
                  { href: '/categorie/conservation-et-cuisson', label: 'Conservation & Cuisson' },
                  { href: '/categorie/nettoyage-et-entretien', label: 'Nettoyage & Entretien' },
                  { href: '/categorie/nos-coups-de-coeur', label: 'Nos Coups de Cœur' },
                  { href: '/categorie/meilleures-ventes', label: 'Meilleures Ventes' },
                ].map((cat) => (
                  <Link
                    key={cat.href}
                    href={cat.href}
                    className="group flex items-center justify-start text-sm text-purple-100 transition-colors hover:text-white"
                  >
                    <span className="hidden lg:block mr-2 h-1 w-1 rounded-full bg-white opacity-40 transition-all group-hover:w-3 group-hover:opacity-100" />
                    {cat.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Newsletter Section */}
            <div className="flex flex-col items-start">
              <h4 className="mb-4 text-lg font-bold uppercase tracking-wider">Offres exclusives</h4>
              <p className="mb-6 text-sm text-purple-100">Inscrivez-vous pour recevoir nos promotions par SMS.</p>

              <form onSubmit={handleNewsletterSubmit} className="w-full max-w-[300px] lg:max-w-sm">
                <div className="flex overflow-hidden rounded-xl bg-white/10 backdrop-blur-md border border-white/20 p-1 transition-all focus-within:border-white/50 focus-within:ring-2 focus-within:ring-white/10">
                  <div className="flex items-center pl-3 text-purple-200">
                    <FiPhone className="h-4 w-4" />
                  </div>
                  <input
                    type="tel"
                    placeholder="Numéro (8 chiffres)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-transparent px-3 py-2 text-sm outline-none placeholder:text-purple-300 text-white"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center rounded-lg bg-white px-3 py-2 text-[#a81b8d] transition-all hover:bg-purple-50 active:scale-90 disabled:opacity-50"
                  >
                    {loading ? <LucideLoader className="h-4 w-4 animate-spin" /> : <FiSend className="h-4 w-4" />}
                  </button>
                </div>

                {status && (
                  <div className={`mt-4 flex items-center gap-2 rounded-lg p-3 text-xs animate-in slide-in-from-top-2 duration-300 ${status.isError ? 'bg-red-500/30 text-white' : 'bg-emerald-500/30 text-white'
                    }`}>
                    {status.isError ? <Alert size={14} /> : <Check size={14} />}
                    <span className="flex-1">{status.message}</span>
                  </div>
                )}
              </form>

              {/* Social Icons */}
              <div className="mt-8 flex gap-4 justify-start">
                {[
                  { icon: <FaFacebookF />, href: "https://www.facebook.com/profile.php?id=61586076066296" },
                  { icon: <FaInstagram />, href: "https://www.instagram.com/parex_tunisie/" },
                  { icon: <FaTiktok />, href: "#" },
                  { icon: <FaWhatsapp />, href: "https://wa.me/21698148481" },
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-white transition-all hover:bg-white hover:text-[#a81b8d] hover:-translate-y-1 active:scale-90"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Contact Section */}
            <div className="flex flex-col items-start">
              <h4 className="mb-6 text-lg font-bold uppercase tracking-wider">Service Commercial</h4>
              <address className="not-italic text-sm text-purple-100 space-y-4 text-left">
                <div className="flex flex-col gap-1">
                  <span className="font-bold text-white">Adresse :</span>
                  <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    54 Rue du Mercure, <br /> 2013 Ben Arous, Tunisie
                  </a>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <a href="mailto:contact@parex.tn" className="hover:underline block text-purple-200">contact@parex.tn</a>
                  <a href="tel:+21698148481" className="text-xl font-black text-white hover:scale-105 transition-transform inline-block">
                    +216 98 148 481
                  </a>
                </div>
              </address>
            </div>

          </div>

          {/* Partners Section */}
          <div className="mt-16 border-t border-white/10 pt-10">
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-12">
              <Image src="/logo.png.webp" alt="Partner TVS" width={80} height={40} className="hover:opacity-100 transition-all cursor-pointer h-auto w-auto max-h-[35px]" />
              <Image src="/gei.svg" alt="Partner GEI" width={80} height={40} className="hover:opacity-100 transition-all cursor-pointer h-auto w-auto max-h-[35px]" />
            </div>
          </div>

          {/* Copyright Bar */}
          <div className="mt-12 text-center border-t border-white/10 pt-8">
            <p className="text-[10px] sm:text-xs font-medium text-white uppercase tracking-[0.2em]">
              © {currentYear} GEI. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}